// =============================================================================
// NewsPage — rota /news standalone (linkada pelo launcher do client).
// Layout: header simples (logo + voltar) + lista completa de noticias
// (mesma API getNews) + footer minimo. Sem hero/sistemas/ranking — direto
// no que o launcher precisa exibir num iframe / browser embed.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Newspaper } from "lucide-react";
import { getNews, type NewsItem } from "@/shared/services/eventsService";
import { Brand } from '@/shared/components/liberty';
import { newsImg, newsImgFallback } from '@/shared/utils/newsImg';

export default function NewsPage() {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getNews()
      .then(setNews)
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = useMemo(
    () => (d: string) => new Date(d).toLocaleDateString("pt-BR"),
    []
  );

  const stripHtml = (s: string) => {
    if (!s) return "";
    const noTags = s.replace(/<[^>]+>/g, " ");
    if (typeof document === "undefined") return noTags.replace(/\s+/g, " ").trim();
    const t = document.createElement("textarea");
    t.innerHTML = noTags;
    return t.value.replace(/\s+/g, " ").trim();
  };

  return (
    <div className="lb-news min-h-screen flex flex-col bg-[#05080b] text-white">
      {/* === HEADER === */}
      <header className="border-b border-white/10 bg-black/55 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-[#00e1d7] transition-colors cursor-pointer"
          >
            <ChevronLeft size={16} /> Voltar ao site
          </button>
          <Brand />
        </div>
      </header>

      {/* === MAIN === */}
      <main className="flex-1 mx-auto max-w-5xl px-4 py-10 w-full">
        <h1 className="lb-news-title mb-2">
          NOTÍCIAS
        </h1>
        <p className="text-sm text-white/45 mb-8">Tudo o que está acontecendo no Cabal Liberty.</p>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-[#00e1d7]" />
          </div>
        ) : news.length === 0 ? (
          <p className="text-center text-white/40 py-16">Nenhuma notícia disponível.</p>
        ) : (
          <div className="space-y-4">
            {news.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(`/news/${item.id}`)}
                className="group cursor-pointer rounded-xl border border-white/10 bg-black/45 backdrop-blur-md p-5 flex items-start gap-5 transition-all hover:border-[#00e1d7]/40 hover:bg-black/55"
              >
                {/* Thumb */}
                <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gradient-to-br from-[#00e1d7]/10 to-black/40">
                  {item.image ? (
                    <img src={newsImg(item.image, 320)} onError={newsImgFallback(item.image)} loading="lazy" alt={item.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Newspaper size={28} className="text-[#00e1d7]/30" />
                    </div>
                  )}
                </div>
                {/* Texto */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-[#00e1d7]/15 text-[#00e1d7] border border-[#00e1d7]/30">
                      Notícia
                    </span>
                    <span className="text-[11px] text-white/40 font-medium tracking-wider">{fmtDate(item.createdDate)}</span>
                  </div>
                  <h3 className="text-lg font-extrabold text-white leading-tight tracking-tight group-hover:text-[#00e1d7] transition-colors uppercase">
                    {item.title}
                  </h3>
                  {item.subTitle && (
                    <p className="mt-1 text-sm font-semibold text-[#00e1d7]/80">{item.subTitle}</p>
                  )}
                  <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-2">
                    {stripHtml(item.html)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* === FOOTER === */}
      <footer className="border-t border-white/8 bg-black/40 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-4 py-5 text-center">
          <p className="text-xs text-white/35">
            &copy; {new Date().getFullYear()} Liberty Games · Cabal EP39
          </p>
        </div>
      </footer>

    </div>
  );
}
